import * as React from "react";
import axios, { AxiosError } from "axios";
import moment from "moment";
import { useNavigate } from "react-router";
import { ActivationsContext, ActivationsContextType } from "./Context";
import { buildDefaultWithExtraData } from "../../services/data";
import { trackEvent } from "../../services/analytics_v2";

import RequestDateOfBirthForm, {
  FormValuesType,
} from "../../components/RequestDateOfBirthForm/RequestDateOfBirthForm";
import { SalesForceUserLookupResponse } from "../../services/salesforce";

const loader = require("../../images/puff.svg") as string;

export default function RetrieveDetails() {
  const [appData, setAppData] =
    React.useContext<ActivationsContextType>(ActivationsContext);
  const [fetchingDetails, setFetchingDetails] = React.useState<null | boolean>(
    null,
  );
  const [fetchError, setFetchError] = React.useState<null | string>(null);

  const navigate = useNavigate();

  /**
   * Attempt to fetch the data and either store it and continue, or return an
   * error message if we failed
   */
  const handleSubmit = async (details: FormValuesType) => {
    trackEvent("v2activation-retrieve_details-detail_lookup_submit");

    const transformedDate = moment(details.dateOfBirth, "DD/MM/YYYY", true);

    try {
      const url = `/campaign_members/${
        appData.params.campaignmember_id
      }?&dateOfBirth=${transformedDate.format("YYYY-MM-DD")}`;

      setFetchingDetails(true);

      // TODO: We should handle the timeout here
      const response = await axios({
        url,
        timeout: 20000,
        method: "GET",
      });

      trackEvent("v2activation-retrieve_details-detail_lookup_success");
      setFetchingDetails(false);

      // Extract and store the returned data
      const salesForceData = response.data as SalesForceUserLookupResponse;

      // Build the form values using the data returned from Salesforce
      setAppData({
        ...appData,
        patientEnrolmentFormData: {
          ...buildDefaultWithExtraData(salesForceData),
          ...(salesForceData.dob
            ? {
                dob: moment(salesForceData.dob, "DD/MM/YYYY").format(
                  "YYYY-MM-DD",
                ),
              }
            : {}),
        },
      });

      // It was a success, go to the form
      navigate(
        `/activations/enrol?campaignmember_id=${appData.params.campaignmember_id}`,
      );
    } catch (error: unknown) {
      trackEvent("v2activation-retrieve_details-detail_lookup_failure");

      // We can't place this in the "finally" block as if the request was
      // successful, it would be unmounted by then.
      setFetchingDetails(false);

      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        if (!axiosError.response) {
          throw new Error("An unknown Axios error was thrown");
        }

        switch (axiosError.response.status) {
          case 400:
            setFetchError("Your date of birth was incorrect");
            break;
          case 404:
            setFetchError("We could not find your details");
            break;
          case 429:
            setFetchError("Sorry, too many tries to fetch data");
            break;
        }
      } else {
        console.error(error);

        throw new Error("We had an unknown error trying to fetch your details");
      }
    }
  };

  const renderedFetchError = (() => {
    if (fetchError === null) {
      return null;
    }

    return (
      <p className="text-center text-red-500 font-bold mt-4">{fetchError}</p>
    );
  })();

  // Show a busy indicator as we fetch the data
  if (fetchingDetails === true) {
    return (
      <div
        className="flex flex-col justify-center"
        style={{ minHeight: "300px" }}
      >
        <div className="flex items-center mx-auto">
          <img alt="Loading" src={loader} />
          <div className="ml-4 text-xl">
            Attempting to retrieve your details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <h2>Regular diabetes eye checks help protect your vision</h2>
      <p>
        To get free reminders for your future eye checks and see when you are
        due, you can sign up to KeepSight.
      </p>
      <p>
        KeepSight is an eye check reminder program delivered by Diabetes
        Australia and supported by the Australian Government.
      </p>
      <p>
        To make it easier to sign up and see if you're due, we can retrieve your
        details from the NDSS.
      </p>
      <p className="mb-0">Just enter your date of birth below.</p>
      <div className="my-6">
        <RequestDateOfBirthForm onSubmit={(details) => handleSubmit(details)} />
        {renderedFetchError}
      </div>
    </>
  );
}
